import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { openProfileDrawer } from './profileDrawerHelper';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { SIGN_DISCLOSING_PARTY_NDA } from '../../../graphql/mutations/dealMutations';

// Lightweight inline signature pad using HTML canvas — no external dependency
const SignaturePad = React.forwardRef(({ width, height, className }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  };

  const onStart = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const onEnd = () => { drawing.current = false; };

  // Expose clear() and isEmpty() and toDataURL() via ref
  React.useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    },
    isEmpty: () => {
      const canvas = canvasRef.current;
      const data = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height).data;
      return !data.some(channel => channel !== 0);
    },
    toDataURL: (type) => canvasRef.current.toDataURL(type),
  }));

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{ touchAction: 'none', cursor: 'crosshair' }}
      onMouseDown={onStart}
      onMouseMove={onMove}
      onMouseUp={onEnd}
      onMouseLeave={onEnd}
      onTouchStart={onStart}
      onTouchMove={onMove}
      onTouchEnd={onEnd}
    />
  );
});

const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      agent_word
      agent_word_fr
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

const GET_DEAL_NDA = gql`
  query GetDealNDAForDiscloser($dealId: ID!) {
    get_deal(name: $dealId) {
      id
      nda {
        id
        title
        legal_text
      }
    }
  }
`;

const DealNdaAwaitingSignature = ({ deal, refetchDeal }) => {
  const intl = useIntl();
  const { data: settingsData } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'cache-first'
  });
  const companyData = settingsData?.company || {};
  const companySettings = {
    avatar_directory: companyData.avatar_directory,
    avatar_background_color: companyData.avatar_background_color,
    primary_color: companyData.primary_color,
  };
  const agentWord = (intl.locale === 'fr' ? (companyData.agent_word_fr || 'Conseiller(ère)') : (companyData.agent_word || 'advisor')).toLowerCase();
  const agentWordLabel = agentWord.charAt(0).toUpperCase() + agentWord.slice(1);

  // Modal state
  const [signingInterest, setSigningInterest] = useState(null);
  const [signName, setSignName] = useState('');
  const [signCompanyName, setSignCompanyName] = useState('');
  const [signTitle, setSignTitle] = useState('');
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [signError, setSignError] = useState('');
  const [signSuccess, setSignSuccess] = useState(false);
  const sigPadRef = useRef(null);

  const { data: ndaData } = useQuery(GET_DEAL_NDA, {
    variables: { dealId: deal?.deal_name || deal?.id },
    skip: !signingInterest || !deal,
  });

  const [signDisclosingPartyNda, { loading: signingInProgress }] = useMutation(SIGN_DISCLOSING_PARTY_NDA);

  // Filter deal interests to show NDAs awaiting your signature
  const ndaAwaitingSignatureInterests = deal?.nda_awaiting_signature?.filter(
    interest => interest.workflow_state === 'awaiting_disclosing_signature'
  ) || [];

  const openSignModal = (interest) => {
    setSigningInterest(interest);
    setSignName('');
    setSignCompanyName('');
    setSignTitle('');
    setAgreementChecked(false);
    setSignError('');
    setSignSuccess(false);
  };

  const closeSignModal = () => {
    setSigningInterest(null);
    setSignSuccess(false);
    setSignError('');
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
  };

  const clearSignature = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignError('');

    if (!signName.trim()) {
      setSignError(intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.full_name_placeholder' }));
      return;
    }
    if (!signCompanyName.trim()) {
      setSignError(intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.company_name_placeholder' }));
      return;
    }
    if (!signTitle.trim()) {
      setSignError(intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.title_placeholder' }));
      return;
    }
    if (!agreementChecked) {
      setSignError(intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.agree_label' }));
      return;
    }
    if (sigPadRef.current && sigPadRef.current.isEmpty()) {
      setSignError(intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.signature_label' }));
      return;
    }

    const signatureData = sigPadRef.current.toDataURL('image/png');

    try {
      const result = await signDisclosingPartyNda({
        variables: {
          dealId: String(deal.id),
          dealInterestId: String(signingInterest.id),
          signatureData,
        }
      });

      const errors = result?.data?.signDisclosingPartyNda?.errors;
      if (errors && errors.length > 0) {
        setSignError(errors[0]);
        return;
      }

      setSignSuccess(true);
      refetchDeal();
    } catch (err) {
      setSignError(intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.failed_to_sign' }));
      console.error('NDA signing error:', err);
    }
  };

  const nda = ndaData?.get_deal?.nda || {};

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {intl.formatMessage({ id: 'dealNda.awaitingSignature.title' })}
        </h2>
        <p className="text-gray-600">
          {ndaAwaitingSignatureInterests.length === 0
            ? intl.formatMessage({ id: 'dealNda.awaitingSignature.empty_count' })
            : intl.formatMessage({ id: 'dealNda.awaitingSignature.count' }, { count: ndaAwaitingSignatureInterests.length })}
        </p>
      </div>

      {ndaAwaitingSignatureInterests.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {agentWordLabel}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{intl.formatMessage({ id: 'dealNda.awaitingSignature.column.location' })}</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{intl.formatMessage({ id: 'dealNda.awaitingSignature.column.status' })}</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">{intl.formatMessage({ id: 'dealNda.awaitingSignature.column.actions' })}</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ndaAwaitingSignatureInterests.map(interest => {
                const advisor = interest?.inventory_user?.user;
                if (!advisor) return null;
                const avatarStyle = getAvatarStyle(advisor.profile_picture, companySettings);

                return (
                  <tr key={interest.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="h-10 w-10 rounded-full overflow-hidden flex items-center justify-center text-gray-500 text-sm font-semibold"
                          style={{ backgroundColor: avatarStyle.backgroundColor }}
                        >
                          <img src={avatarStyle.url} alt="" className="w-10 h-10 rounded-full object-cover" style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{advisor?.name || intl.formatMessage({ id: 'dealNda.awaitingSignature.unknown' })}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {advisor?.city ? `${advisor.city}${advisor.province ? `, ${advisor.province}` : ''}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        {intl.formatMessage({ id: 'dealNda.awaitingSignature.status.required' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            if (advisor?.fb_user_name) {
                              openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest: interest });
                            }
                          }}
                          className="text-blue-600 hover:text-blue-900 bg-transparent border-0 cursor-pointer"
                        >
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.action.view_details' })}
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => openSignModal(interest)}
                          className="text-blue-600 hover:text-blue-900 font-medium bg-transparent border-0 cursor-pointer"
                        >
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.action.sign_nda' })}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-10 text-center">
          <div className="flex flex-col items-center justify-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{intl.formatMessage({ id: 'dealNda.awaitingSignature.empty_state.title' })}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {intl.formatMessage({ id: 'dealNda.awaitingSignature.empty_state.description' })}
            </p>
          </div>
        </div>
      )}

      {/* Signing Modal */}
      {signingInterest && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="nda-sign-modal" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeSignModal} aria-hidden="true" />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                {signSuccess ? (
                  <div className="text-center py-6">
                    <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h2 className="mt-4 text-xl font-bold text-gray-900">
                      {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.success_heading' })}
                    </h2>
                    <p className="mt-2 text-gray-600">
                      {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.success_description' })}
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={closeSignModal}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.close' })}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.title' })}
                    </h3>

                    {signError && (
                      <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
                        <span className="block sm:inline">{signError}</span>
                      </div>
                    )}

                    {nda.legal_text && (
                      <div className="mb-4">
                        <h4 className="text-base font-semibold mb-2">{nda.title || intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.nda_fallback_title' })}</h4>
                        <div className="border border-gray-300 rounded-md p-4 h-48 overflow-y-auto bg-gray-50 mb-3">
                          <div dangerouslySetInnerHTML={{ __html: nda.legal_text }} />
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="nda-agreement"
                            type="checkbox"
                            checked={agreementChecked}
                            onChange={(e) => setAgreementChecked(e.target.checked)}
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="nda-agreement" className="font-medium text-gray-700">
                            {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.agree_label' })}
                          </label>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="nda-sign-name" className="block text-sm font-medium text-gray-700">
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.full_name_label' })}
                        </label>
                        <input
                          type="text"
                          id="nda-sign-name"
                          value={signName}
                          onChange={(e) => setSignName(e.target.value)}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder={intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.full_name_placeholder' })}
                        />
                      </div>

                      <div>
                        <label htmlFor="nda-sign-company" className="block text-sm font-medium text-gray-700">
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.company_name_label' })}
                        </label>
                        <input
                          type="text"
                          id="nda-sign-company"
                          value={signCompanyName}
                          onChange={(e) => setSignCompanyName(e.target.value)}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder={intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.company_name_placeholder' })}
                        />
                      </div>

                      <div>
                        <label htmlFor="nda-sign-title" className="block text-sm font-medium text-gray-700">
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.title_label' })}
                        </label>
                        <input
                          type="text"
                          id="nda-sign-title"
                          value={signTitle}
                          onChange={(e) => setSignTitle(e.target.value)}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder={intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.title_placeholder' })}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.signature_label' })}
                        </label>
                        <div className="border border-gray-300 rounded-md overflow-hidden bg-white">
                          <SignaturePad
                            ref={sigPadRef}
                            width={560}
                            height={160}
                            className="w-full h-40"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={clearSignature}
                          className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.clear_signature' })}
                        </button>
                      </div>

                      <div className="flex justify-end space-x-3 pt-2">
                        <button
                          type="button"
                          onClick={closeSignModal}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          {intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.cancel' })}
                        </button>
                        <button
                          type="submit"
                          disabled={signingInProgress}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {signingInProgress
                            ? intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.signing' })
                            : intl.formatMessage({ id: 'dealNda.awaitingSignature.sign_modal.sign_nda' })}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DealNdaAwaitingSignature.propTypes = {
  deal: PropTypes.object.isRequired,
  refetchDeal: PropTypes.func.isRequired
};

export default DealNdaAwaitingSignature;
